import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Plus, Trash2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import ActivityImagePreview from '@/components/ActivityImagePreview';
import CustomImageUpload from '@/components/CustomImageUpload';
import PredefinedImageSelector from '@/components/PredefinedImageSelector';
import ActivityCategorySelector from '@/components/ActivityCategorySelector';
import ImageUploadErrorBoundary from '@/components/ImageUploadErrorBoundary';
import { activityApi } from '@/services/adminApi';
import { useCreateActivity } from '@/hooks/useCreateActivity';
import { useCategories } from '@/hooks/useActivities';
import {
  type Tag,
  type Transition,
  type CreateActivityFormData,
  type ActivityStep,
  type ActivityTip,
  PLAY_TYPE_OPTIONS,
  PREP_NEEDED_OPTIONS,
  AGE_GROUP_OPTIONS,
} from '@/types/activity';
import type { ActivityImageValue } from '@/types/upload';

// ============================================
// Initial Form State
// ============================================

const initialFormData: CreateActivityFormData = {
  title: '',
  description: '',
  image: null,
  documentId: null,
  predefinedActivityImageId: null,
  imageType: null,
  duration: '',
  playType: '',
  prepNeeded: '',
  steps: [{ text: '', emphasis: '' }],
  tips: [{ text: '', boldText: '' }],
  whyItWorks: { text: '', boldKeywords: [] },
  tagIds: [],
  transitionIds: [],
  ageGroupKeys: [],
  categoryId: null,
};

// ============================================
// Form Validation
// ============================================

interface FormErrors {
  title?: string;
  description?: string;
  duration?: string;
  playType?: string;
  prepNeeded?: string;
  steps?: string;
  tips?: string;
  whyItWorks?: string;
  tags?: string;
}

function validateForm(data: CreateActivityFormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.title.trim()) {
    errors.title = 'Title is required';
  } else if (data.title.length > 255) {
    errors.title = 'Title must be 255 characters or less';
  }

  if (!data.description.trim()) {
    errors.description = 'Description is required';
  } else if (data.description.length > 1000) {
    errors.description = 'Description must be 1000 characters or less';
  }

  if (data.duration === '' || data.duration < 1) {
    errors.duration = 'Duration must be at least 1 minute';
  } else if (data.duration > 600) {
    errors.duration = 'Duration must be 600 minutes or less';
  }

  if (!data.playType) {
    errors.playType = 'Play type is required';
  }

  if (!data.prepNeeded) {
    errors.prepNeeded = 'Prep needed is required';
  }

  const validSteps = data.steps.filter(s => s.text.trim() !== '');
  if (validSteps.length === 0) {
    errors.steps = 'At least 1 step is required';
  }

  const validTips = data.tips.filter(t => t.text.trim() !== '');
  if (validTips.length === 0) {
    errors.tips = 'At least 1 tip is required';
  }

  if (!data.whyItWorks.text.trim()) {
    errors.whyItWorks = 'Why it works explanation is required';
  }

  if (data.tagIds.length === 0) {
    errors.tags = 'At least 1 tag is required';
  }

  return errors;
}


  const inputClassName = "w-full px-4 py-3 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 placeholder-admin-slate-600 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors";
  const selectClassName = "w-full px-4 py-3 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors";
  const errorClassName = "text-red-400 text-sm mt-1";

// ============================================
// Component
// ============================================

export default function NewActivityPage() {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState<CreateActivityFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  
  // Tags state
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);

  // Transitions state
  const [availableTransitions, setAvailableTransitions] = useState<Transition[]>([]);
  const [transitionsLoading, setTransitionsLoading] = useState(true);

  // Categories
  const { data: categoriesData } = useCategories();
  const availableCategories = categoriesData?.data || [];

  // Image type switching state
  const [imageWarning, setImageWarning] = useState<string | null>(null);
  const [lastCustom, setLastCustom] = useState<ActivityImageValue | null>(null);
  const [lastPredefined, setLastPredefined] = useState<ActivityImageValue | null>(null);

  // Bold keywords input (comma-separated)
  const [boldKeywordsInput, setBoldKeywordsInput] = useState('');

  // Create activity mutation
  const createActivityMutation = useCreateActivity();

  // ============================================
  // Load Tags on Mount
  // ============================================
  
  useEffect(() => {
    async function loadTags() {
      try {
        const response = await activityApi.getTags();
        setAvailableTags(response.data);
      } catch (error) {
        toast.error('Failed to load tags');
        console.error('Failed to load tags:', error);
      } finally {
        setTagsLoading(false);
      }
    }
    loadTags();
  }, []);

  useEffect(() => {
    async function loadTransitions() {
      try {
        const response = await activityApi.getTransitions();
        setAvailableTransitions(response.data);
      } catch (error) {
        toast.error('Failed to load time periods');
        console.error('Failed to load time periods:', error);
      } finally {
        setTransitionsLoading(false);
      }
    }
    loadTransitions();
  }, []);

  // ============================================
  // Field Handlers
  // ============================================

  const handleFieldChange = (field: keyof CreateActivityFormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleTagToggle = (tagId: number) => {
    setFormData(prev => {
      const newTagIds = prev.tagIds.includes(tagId)
        ? prev.tagIds.filter(id => id !== tagId)
        : [...prev.tagIds, tagId];
      return { ...prev, tagIds: newTagIds };
    });
    if (errors.tags) {
      setErrors(prev => ({ ...prev, tags: undefined }));
    }
  };

  const handleTransitionToggle = (transitionId: number) => {
    setFormData(prev => {
      const newTransitionIds = prev.transitionIds.includes(transitionId)
        ? prev.transitionIds.filter(id => id !== transitionId)
        : [...prev.transitionIds, transitionId];
      return { ...prev, transitionIds: newTransitionIds };
    });
  };

  const handleAgeGroupToggle = (key: string) => {
    setFormData(prev => {
      const newKeys = prev.ageGroupKeys.includes(key)
        ? prev.ageGroupKeys.filter(k => k !== key)
        : [...prev.ageGroupKeys, key];
      return { ...prev, ageGroupKeys: newKeys };
    });
  };

  // ============================================
  // Steps Handlers
  // ============================================

  const handleStepChange = (index: number, field: keyof ActivityStep, value: string) => {
    setFormData(prev => {
      const newSteps = [...prev.steps];
      newSteps[index] = { ...newSteps[index], [field]: value };
      return { ...prev, steps: newSteps };
    });
    if (errors.steps) {
      setErrors(prev => ({ ...prev, steps: undefined }));
    }
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { text: '', emphasis: '' }],
    }));
  };

  const removeStep = (index: number) => {
    if (formData.steps.length > 1) {
      setFormData(prev => ({
        ...prev,
        steps: prev.steps.filter((_, i) => i !== index),
      }));
    }
  };

  // ============================================
  // Tips Handlers
  // ============================================

  const handleTipChange = (index: number, field: keyof ActivityTip, value: string) => {
    setFormData(prev => {
      const newTips = [...prev.tips];
      newTips[index] = { ...newTips[index], [field]: value };
      return { ...prev, tips: newTips };
    });
    if (errors.tips) {
      setErrors(prev => ({ ...prev, tips: undefined }));
    }
  };

  const addTip = () => {
    setFormData(prev => ({
      ...prev,
      tips: [...prev.tips, { text: '', boldText: '' }],
    }));
  };

  const removeTip = (index: number) => {
    if (formData.tips.length > 1) {
      setFormData(prev => ({
        ...prev,
        tips: prev.tips.filter((_, i) => i !== index),
      }));
    }
  };

  // ============================================
  // Why It Works Handlers
  // ============================================

  const handleWhyItWorksTextChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      whyItWorks: { ...prev.whyItWorks, text: value },
    }));
    if (errors.whyItWorks) {
      setErrors(prev => ({ ...prev, whyItWorks: undefined }));
    }
  };

  const handleBoldKeywordsChange = (value: string) => {
    setBoldKeywordsInput(value);
    const keywords = value
      .split(',')
      .map(k => k.trim())
      .filter(k => k !== '');
    setFormData(prev => ({
      ...prev,
      whyItWorks: { ...prev.whyItWorks, boldKeywords: keywords },
    }));
  };

  // ============================================
  // Image Type Activation Handlers
  // ============================================

  const handleActivateCustom = () => {
    if (lastCustom) {
      setFormData((prev) => ({ ...prev, image: lastCustom, imageType: 'custom', documentId: lastCustom.documentId ?? null, predefinedActivityImageId: null }));
      setImageWarning(null);
    } else {
      setImageWarning('Upload a custom image first');
    }
  };

  const handleActivatePredefined = () => {
    if (lastPredefined) {
      setFormData((prev) => ({ ...prev, image: lastPredefined, imageType: 'predefined', documentId: null, predefinedActivityImageId: lastPredefined.predefinedActivityImageId ?? null }));
      setImageWarning(null);
    } else {
      setImageWarning('Select a predefined image first');
    }
  };

  const handleActivateCategory = () => {
    const cat = availableCategories.find((c) => parseInt(c.id, 10) === formData.categoryId);
    if (cat?.imageName) {
      const catValue: ActivityImageValue = { type: 'category', categoryId: formData.categoryId!, viewUrl: cat.imageName };
      setFormData((prev) => ({ ...prev, image: catValue, imageType: 'category', documentId: null, predefinedActivityImageId: null }));
      setImageWarning(null);
    } else if (!formData.categoryId) {
      setImageWarning('Select a category first');
    } else {
      setImageWarning('This category has no image');
    }
  };

  // ============================================
  // Form Submit
  // ============================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the form errors');
      return;
    }

    try {
      await createActivityMutation.mutateAsync(formData);
      navigate('/admin/activities');
    } catch (error) {
      // Error already handled by the mutation hook
      console.error('Failed to create activity:', error);
    }
  };

  // ============================================
  // Render
  // ============================================
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/activities')}
          className="mb-4 text-admin-slate-500 hover:text-admin-slate-400"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Activities
        </Button>

        <h1 className="text-heading-1 text-admin-slate-400 mb-2">
        New Activity
        </h1>
      </motion.div>

      <form onSubmit={handleSubmit}>
        {/* Basic Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-6"
        >
          <Card className="bg-admin-dark-800 border-admin-slate-300/10">
            <CardContent className="space-y-6 mt-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title" className="text-admin-slate-400">
                  Activity Title <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder="e.g., Sentence Builders"
                  className={inputClassName}
                />
                {errors.title && <p className={errorClassName}>{errors.title}</p>}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-admin-slate-400">
                  Description <span className="text-red-400">*</span>
                </Label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Brief description of the activity"
                  className={`${inputClassName} resize-none`}
                />
                {errors.description && <p className={errorClassName}>{errors.description}</p>}
              </div>

              {/* Image Section */}
              <ImageUploadErrorBoundary>
                {formData.image && 'type' in formData.image && (
                  <ActivityImagePreview
                    value={formData.image}
                    onRemove={() => {
                      setFormData((prev) => ({ ...prev, image: null, imageType: null, documentId: null, predefinedActivityImageId: null }));
                      setImageWarning(null);
                    }}
                  />
                )}

                <div className="space-y-4 rounded-lg border border-admin-slate-300/10 p-4">
                  <CustomImageUpload
                    isActive={formData.image && 'type' in formData.image ? formData.image.type === 'custom' : false}
                    onActivate={handleActivateCustom}
                    onUpload={(val) => {
                      setLastCustom(val);
                      setFormData((prev) => ({ ...prev, image: val, imageType: 'custom', documentId: val.documentId ?? null, predefinedActivityImageId: null }));
                      setImageWarning(null);
                    }}
                    warning={!(formData.image && 'type' in formData.image && formData.image.type === 'custom') ? imageWarning : null}
                  />

                  <PredefinedImageSelector
                    isActive={formData.image && 'type' in formData.image ? formData.image.type === 'predefined' : false}
                    selectedImageId={lastPredefined?.predefinedActivityImageId ?? null}
                    onActivate={handleActivatePredefined}
                    onSelect={(val) => {
                      setLastPredefined(val);
                      setFormData((prev) => ({ ...prev, image: val, imageType: 'predefined', documentId: null, predefinedActivityImageId: val.predefinedActivityImageId ?? null }));
                      setImageWarning(null);
                    }}
                    warning={!(formData.image && 'type' in formData.image && formData.image.type === 'predefined') ? imageWarning : null}
                  />
                </div>

                <div className="rounded-lg border border-admin-slate-300/10 p-4">
                  <ActivityCategorySelector
                    isActive={formData.image && 'type' in formData.image ? formData.image.type === 'category' : false}
                    categories={availableCategories}
                    selectedCategoryId={formData.categoryId}
                    onCategoryChange={(categoryId) => {
                      setFormData((prev) => ({ ...prev, categoryId }));
                    }}
                    onActivate={handleActivateCategory}
                    warning={!(formData.image && 'type' in formData.image && formData.image.type === 'category') ? imageWarning : null}
                  />
                </div>
              </ImageUploadErrorBoundary>

              {/* Duration, Play Type, Prep Needed Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Duration */}
                <div className="space-y-2">
                  <Label htmlFor="duration" className="text-admin-slate-400">
                    Duration (minutes) <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="duration"
                    type="number"
                    min={1}
                    max={600}
                    value={formData.duration}
                    onChange={(e) => handleFieldChange('duration', e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="e.g., 10"
                    className={inputClassName}
                  />
                  {errors.duration && <p className={errorClassName}>{errors.duration}</p>}
                </div>

                {/* Play Type */}
                <div className="space-y-2">
                  <Label htmlFor="playType" className="text-admin-slate-400">
                    Play Type <span className="text-red-400">*</span>
                  </Label>
                  <select
                    id="playType"
                    value={formData.playType}
                    onChange={(e) => handleFieldChange('playType', e.target.value)}
                    className={selectClassName}
                  >
                    <option value="">Select play type</option>
                    {PLAY_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {errors.playType && <p className={errorClassName}>{errors.playType}</p>}
                </div>

                {/* Prep Needed */}
                <div className="space-y-2">
                  <Label htmlFor="prepNeeded" className="text-admin-slate-400">
                    Prep Needed <span className="text-red-400">*</span>
                  </Label>
                  <select
                    id="prepNeeded"
                    value={formData.prepNeeded}
                    onChange={(e) => handleFieldChange('prepNeeded', e.target.value)}
                    className={selectClassName}
                  >
                    <option value="">Select prep needed</option>
                    {PREP_NEEDED_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {errors.prepNeeded && <p className={errorClassName}>{errors.prepNeeded}</p>}
                </div>
              </div>


              {/* Tags */}
              <div className="space-y-2">
                <Label className="text-admin-slate-400">
                  Tags <span className="text-red-400">*</span>
                </Label>
                {tagsLoading ? (
                  <div className="flex items-center gap-2 text-admin-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading tags...
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleTagToggle(parseInt(tag.id))}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          formData.tagIds.includes(parseInt(tag.id))
                            ? 'bg-admin-accent-blue text-white'
                            : 'bg-admin-dark-700 text-admin-slate-400 hover:bg-admin-dark-600'
                        }`}
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                )}
                {errors.tags && <p className={errorClassName}>{errors.tags}</p>}
              </div>

              {/* Time Periods */}
              <div className="space-y-2">
                <Label className="text-admin-slate-400">
                  Time Periods <span className="text-admin-slate-600">(optional)</span>
                </Label>
                {transitionsLoading ? (
                  <div className="flex items-center gap-2 text-admin-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading time periods...
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableTransitions.map(transition => (
                      <button
                        key={transition.id}
                        type="button"
                        onClick={() => handleTransitionToggle(parseInt(transition.id))}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          formData.transitionIds.includes(parseInt(transition.id))
                            ? 'bg-amber-500 text-white'
                            : 'bg-admin-dark-700 text-admin-slate-400 hover:bg-admin-dark-600'
                        }`}
                      >
                        {transition.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Age Groups */}
              <div className="space-y-2">
                <Label className="text-admin-slate-400">
                  Age Groups <span className="text-admin-slate-600">(optional)</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {AGE_GROUP_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => handleAgeGroupToggle(option.key)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        formData.ageGroupKeys.includes(option.key)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {option.label} yrs
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Steps Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-6"
        >
          <Card className="bg-admin-dark-800 border-admin-slate-300/10">
            <CardHeader>
              <CardTitle className="text-heading-4 text-admin-slate-400">
                Steps <span className="text-red-400">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.steps.map((step, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-admin-accent-blue flex items-center justify-center text-white font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={step.text}
                      onChange={(e) => handleStepChange(index, 'text', e.target.value)}
                      placeholder="Step instruction text"
                      className={inputClassName}
                    />
                    <Input
                      value={step.emphasis}
                      onChange={(e) => handleStepChange(index, 'emphasis', e.target.value)}
                      placeholder="Word to emphasize (optional)"
                      className={`${inputClassName} text-sm`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStep(index)}
                    disabled={formData.steps.length === 1}
                    className="text-admin-slate-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                onClick={addStep}
                className="text-admin-accent-blue hover:text-blue-400"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Step
              </Button>
              {errors.steps && <p className={errorClassName}>{errors.steps}</p>}
            </CardContent>
          </Card>
        </motion.div>

        {/* Tips Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-6"
        >
          <Card className="bg-admin-dark-800 border-admin-slate-300/10">
            <CardHeader>
              <CardTitle className="text-heading-4 text-admin-slate-400">
                Tips <span className="text-red-400">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.tips.map((tip, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input
                      value={tip.text}
                      onChange={(e) => handleTipChange(index, 'text', e.target.value)}
                      placeholder="Full tip text (e.g., Don't correct - just model the right way)"
                      className={inputClassName}
                    />
                    <Input
                      value={tip.boldText || ''}
                      onChange={(e) => handleTipChange(index, 'boldText', e.target.value)}
                      placeholder="Text to highlight (optional, e.g., Don't correct)"
                      className={inputClassName}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTip(index)}
                    disabled={formData.tips.length === 1}
                    className="text-admin-slate-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                onClick={addTip}
                className="text-admin-accent-blue hover:text-blue-400"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Tip
              </Button>
              {errors.tips && <p className={errorClassName}>{errors.tips}</p>}
            </CardContent>
          </Card>
        </motion.div>

        {/* Why It Works Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-6"
        >
          <Card className="bg-admin-dark-800 border-admin-slate-300/10">
            <CardHeader>
              <CardTitle className="text-heading-4 text-admin-slate-400">
                Why It Works <span className="text-red-400">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="whyItWorksText" className="text-admin-slate-400">
                  Explanation
                </Label>
                <textarea
                  id="whyItWorksText"
                  rows={4}
                  value={formData.whyItWorks.text}
                  onChange={(e) => handleWhyItWorksTextChange(e.target.value)}
                  placeholder="Explain why this activity is beneficial..."
                  className={`${inputClassName} resize-none`}
                />
                {errors.whyItWorks && <p className={errorClassName}>{errors.whyItWorks}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="boldKeywords" className="text-admin-slate-400">
                  Bold Keywords <span className="text-admin-slate-600">(comma-separated)</span>
                </Label>
                <Input
                  id="boldKeywords"
                  value={boldKeywordsInput}
                  onChange={(e) => handleBoldKeywordsChange(e.target.value)}
                  placeholder="e.g., speech and language skills, motor control"
                  className={inputClassName}
                />
                <p className="text-caption text-admin-slate-600">
                  Words or phrases to highlight in bold within the explanation text.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <Separator className="bg-admin-slate-300/10 my-6" />

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex gap-4"
        >
          <Button
            type="submit"
            disabled={createActivityMutation.isPending}
            className="bg-admin-accent-blue hover:bg-blue-600 disabled:opacity-50"
          >
            {createActivityMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save as Draft
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/activities')}
            disabled={createActivityMutation.isPending}
            className="bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400"
          >
            Cancel
          </Button>
        </motion.div>
      </form>
    </div>
  );
}
